import React, { createContext, useRef, RefObject } from 'react';
import { useChain } from 'react-spring';

import { Head } from './seo';

import Layout from '../components/Layout';
import { HeroSectionType, PageSectionType, ReactSpringHook } from 'utils/types';
import {
  HeroContainer as HeroSection,
  HeroContainerProps,
} from '../containers/Enterprise/HeroContainer';
import {
  ExperienceContainer as ExperienceSection,
  ExperienceContainerProps,
} from '../containers/Enterprise/ExperienceContainer';

import {
  OutreachContainer as OutreachSection,
  OutreachContainerProps,
} from '../containers/Enterprise/OutreachContainer';

import {
  InTheNewsContainer as InTheNewsSection,
  InTheNewsContainerProps,
} from '../containers/Enterprise/InTheNewsContainer';

import {
  InsightsContainer as InsightsSection,
  InsightsContainerProps,
} from '../containers/Enterprise/InsightsContainer';

import {
  PartnerContainer as PartnerSection,
  PartnerContainerProps,
} from '../containers/Enterprise/PartnerContainer';

import {
  DisclaimerSectionContainer as MarkdownSection,
  DisclaimerSectionContainerProps,
} from '../containers/Enterprise/DisclaimerSectionContainer';

const components: {
  HeroSection: React.FunctionComponent<HeroContainerProps>;
  ExperienceSection: React.FunctionComponent<ExperienceContainerProps>;
  OutreachSection: React.FunctionComponent<OutreachContainerProps>;
  InTheNewsSection: React.FunctionComponent<InTheNewsContainerProps>;
  InsightsSection: React.FunctionComponent<InsightsContainerProps>;
  PartnerSection: React.FunctionComponent<PartnerContainerProps>;
  MarkdownSection: React.FunctionComponent<DisclaimerSectionContainerProps>;
} = {
  HeroSection,
  ExperienceSection,
  OutreachSection,
  InTheNewsSection,
  InsightsSection,
  PartnerSection,
  MarkdownSection,
};

interface PageContext {
  heroSection?: HeroSectionType;
  sections: PageSectionType[];
  componentName: string;
  title: string;
}

interface Props {
  pageContext: PageContext;
}

export interface RefsContextType {
  [key: string]: RefObject<ReactSpringHook>;
}

export const RefsForAnimationContext = createContext<RefsContextType>({});

const enterpriseTemplate: React.FC<Props> = ({ pageContext }) => {
  const { sections, heroSection } = pageContext;

  if (!sections || !heroSection) {
    return null;
  }

  // Refs will be passed down in RefsForAnimationContext
  const animation1 = useRef<ReactSpringHook>(null);
  const animation2 = useRef<ReactSpringHook>(null);
  const animation3 = useRef<ReactSpringHook>(null);
  const animation4 = useRef<ReactSpringHook>(null);

  // by default useChain will space each animation 500ms apart
  // the 2nd argument overrides this default and defines the timing of the animations:
  // 1st animation happens immediately - 2nd animation starts 450ms after first animation ends
  // 3rd and 4th animations start 750ms after the first animation ends
  useChain(
    [animation1, animation2, animation3, animation4],
    [0, 0.2, 0.75, 0.75],
  );

  return (
    <>
      <Head />
      <Layout>
        <RefsForAnimationContext.Provider
          value={{ animation1, animation2, animation3, animation4 }}
        >
          <HeroSection content={heroSection} />
          {sections.map((section, i: number) => {
            // @ts-ignore
            if (components[section.componentName]) {
              // @ts-ignore
              return React.createElement(components[section.componentName], {
                content: section,
                key: i,
              });
            }
          })}
        </RefsForAnimationContext.Provider>
      </Layout>
    </>
  );
};

export default enterpriseTemplate;
